import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { PageHeader } from '../components/PageHeader.js';
import { api, type OpsService, type OpsQueue } from '../lib/api.js';

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function OperationsPage() {
  usePageTitle('Operations');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['ops'], queryFn: () => api.getOps(), refetchInterval: 4000 });

  const run = useMutation({
    mutationFn: (id: string) => api.runOpsService(id),
    onSuccess: (res) => {
      const s = res.service;
      if (s.lastError) toast.error(`${s.name}: ${s.lastError}`);
      else toast.success(`${s.name}: ${s.lastSummary ?? 'done'}`);
      void qc.invalidateQueries({ queryKey: ['ops'] });
      void qc.invalidateQueries({ queryKey: ['builds'] }); // infra-sync repopulates these
    },
    onError: (e) => toast.error(`Run failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader eyebrow="System" title="Operations" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6 max-w-5xl">
        {isLoading && <div className="text-sm text-text-dimmed">Loading…</div>}

        {/* ── Services ── */}
        <section className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Background services</div>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
            {(data?.services ?? []).map((svc) => (
              <ServiceCard key={svc.id} svc={svc} pending={run.isPending && run.variables === svc.id} onRun={() => run.mutate(svc.id)} />
            ))}
          </div>
        </section>

        {/* ── Job queues ── */}
        <section className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Job queues (Redis)</div>
          <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-text-dimmed" style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="px-4 py-2 text-left font-semibold">Queue</th>
                  {['waiting', 'active', 'completed', 'failed', 'delayed'].map((c) => (
                    <th key={c} className="px-3 py-2 text-right font-semibold">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.queues ?? []).map((q) => (
                  <QueueRow key={q.name} q={q} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function ServiceCard({ svc, pending, onRun }: { svc: OpsService; pending: boolean; onRun: () => void }) {
  const busy = svc.running || pending;
  return (
    <div className="glass rounded-xl p-4 flex flex-col gap-2" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-text-primary">{svc.name}</div>
          <div className="text-[11px] text-text-dimmed font-mono">{svc.id} · {svc.schedule}</div>
        </div>
        {svc.configured
          ? <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ background: 'var(--accent-green)1f', color: 'var(--accent-green)', border: '1px solid var(--accent-green)55' }}>ready</span>
          : <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ background: 'var(--accent-yellow)1f', color: 'var(--accent-yellow)', border: '1px solid var(--accent-yellow)55' }} title="Not configured — see Settings">not configured</span>}
      </div>
      <div className="text-[12px] text-text-secondary leading-snug">{svc.description}</div>
      <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>
        {busy ? (
          <span style={{ color: 'var(--accent-blue)' }}>● running…</span>
        ) : svc.lastError ? (
          <span style={{ color: 'var(--accent-red)' }}>✗ {ago(svc.lastRunAt)} ({svc.lastTrigger}): {svc.lastError}</span>
        ) : svc.lastRunAt ? (
          <span style={{ color: 'var(--accent-green)' }}>✓ {ago(svc.lastRunAt)} ({svc.lastTrigger}): {svc.lastSummary}{svc.lastDurationMs != null ? ` · ${(svc.lastDurationMs / 1000).toFixed(1)}s` : ''}</span>
        ) : (
          <span>not run yet this session</span>
        )}
      </div>
      <div>
        <button
          type="button"
          onClick={onRun}
          disabled={busy || !svc.configured}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
          style={{ background: 'var(--accent-blue)1f', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)55' }}
          title={svc.configured ? 'Run this service now' : 'Configure it first (Settings)'}
        >
          {busy ? 'Running…' : '▶ Run now'}
        </button>
      </div>
    </div>
  );
}

function QueueRow({ q }: { q: OpsQueue }) {
  const c = q.counts;
  const cell = (n: number | undefined, warn = false) => (
    <td className="px-3 py-2 text-right font-mono" style={{ color: warn && n ? 'var(--accent-red)' : 'var(--text-primary)' }}>{n ?? '–'}</td>
  );
  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td className="px-4 py-2">
        <div className="text-text-primary font-mono text-[12px]">{q.name}</div>
        <div className="text-[10px] text-text-dimmed">
          {q.label}
          {!q.hasConsumer && <span style={{ color: 'var(--accent-yellow)' }}> · no consumer</span>}
        </div>
      </td>
      {c ? (
        <>
          {cell(c.waiting)}
          {cell(c.active)}
          {cell(c.completed)}
          {cell(c.failed, true)}
          {cell(c.delayed)}
        </>
      ) : (
        <td className="px-3 py-2 text-right text-[11px]" colSpan={5} style={{ color: 'var(--accent-red)' }}>{q.error ?? 'unavailable'}</td>
      )}
    </tr>
  );
}
